import React from 'react';
import { Truck, MapPin, Package, ShoppingBasket, Sprout, Gift } from 'lucide-react';
import { BOXES, ADD_ONS, STOCK, POSSIBLE_VARIETIES, DELIVERY_COUNTIES, LOCAL_DELIVERY } from '@/lib/constants';
import { BoxOrderDialog } from '@/components/forms/BoxOrderDialog';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import SquareButton from '@/components/SquareButton';
import Seo from '@/components/Seo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function Shop() {
  return (
    <div data-testid="shop-page">
      <Seo
        title="Shop Florida Mango Boxes"
        description="Order premium Florida-grown mango boxes. Nationwide shipping, local pickup in South Florida, and local delivery across Palm Beach, Broward & Miami-Dade counties."
        keywords="mango box, buy Florida mangoes, mango delivery, mango pickup, local mango delivery, Florida fruit box"
        ogImage={STOCK.mango_crate}
      />

      <Hero
        testId="shop-hero"
        eyebrow="Florida Mango Boxes"
        title={<>Tree-ripened, <br className="hidden sm:block" /> shipped <span className="text-[hsl(var(--c-mango-red))]">direct</span>.</>}
        subtitle="Three box sizes, hand-selected fruit, mixed seasonal varieties when available. Nationwide shipping, local pickup, or local delivery across South Florida."
        block="yellow"
        bgImage={STOCK.mango_crate}
        ctas={[
          { label: 'See The Boxes', href: '#shop-boxes', variant: 'ink', testId: 'shop-hero-see-boxes' },
          { label: 'Order Options', href: '#order-options', variant: 'teal', testId: 'shop-hero-order-options' },
        ]}
      />

      {/* PRODUCT CARDS with accordion detail */}
      <section id="shop-boxes" data-testid="shop-box-cards" className="block-cream">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="Shop Mango Boxes"
              size="xl"
              title="Three sizes. One Florida flavor."
              subtitle="Enjoy a seasonal assortment of hand-selected, tree-ripened Florida mangoes harvested at peak freshness. Available varieties change throughout the season based on harvest availability."
            />
          </Reveal>
          <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {BOXES.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.05}>
                <div
                  data-testid={`shop-box-card-${b.slug}`}
                  className="group relative bg-white h-full flex flex-col border-2 border-[hsl(var(--c-ink))] lift-on-hover"
                >
                  {b.popular && (
                    <div className="absolute top-3 right-3 z-10 bg-[hsl(var(--c-mango-red))] text-white text-[10px] tracking-[0.18em] uppercase px-3 py-2 font-bold border-2 border-[hsl(var(--c-ink))] shadow-[3px_3px_0_0_hsl(var(--c-ink))]">Most Popular</div>
                  )}
                  {b.image && (
                    <div className="relative aspect-[4/3] w-full overflow-hidden border-b-2 border-[hsl(var(--c-ink))]">
                      <img src={b.image} alt={`${b.name} — fresh Florida mangoes`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
                      <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 bg-white border-2 border-[hsl(var(--c-ink))] px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold">
                        {b.weight} • {b.count}
                      </div>
                    </div>
                  )}
                  <div className="p-6 sm:p-7 flex flex-col flex-1">
                    <h3 className="font-heading text-2xl sm:text-3xl font-extrabold leading-tight">{b.name}</h3>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="font-heading text-4xl sm:text-5xl text-[hsl(var(--c-mango-red))] font-extrabold">${b.price}</span>
                      <span className="text-xs text-[hsl(var(--c-ink-soft))] uppercase tracking-wider">/ box</span>
                    </div>
                    <p className="mt-3 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{b.blurb}</p>

                    <Accordion type="single" collapsible defaultValue="details" className="mt-4 border-t-2 border-[hsl(var(--c-ink))]/25">
                      <AccordionItem value="details" className="border-b-2 border-[hsl(var(--c-ink))]/25">
                        <AccordionTrigger data-testid={`shop-box-${b.slug}-acc-details`} className="text-xs uppercase tracking-[0.14em] font-bold text-[hsl(var(--c-ink))] hover:no-underline">Product Details</AccordionTrigger>
                        <AccordionContent className="text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">
                          <ul className="space-y-1.5 mb-3">
                            {b.includes.map((x) => (
                              <li key={x} className="flex items-start gap-2">
                                <Sprout className="h-4 w-4 mt-0.5 text-[hsl(var(--c-leaf))] shrink-0" /> {x}
                              </li>
                            ))}
                          </ul>
                          <div className="mt-3">
                            <div className="eyebrow text-[hsl(var(--c-ink))]/60">Possible varieties</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {POSSIBLE_VARIETIES.map((v) => (
                                <span key={v} className="text-[10px] bg-[hsl(var(--c-mango-yellow))] border border-[hsl(var(--c-ink))] text-[hsl(var(--c-ink))] px-2 py-0.5 font-semibold uppercase tracking-wider">{v}</span>
                              ))}
                            </div>
                            <p className="mt-2 text-xs text-[hsl(var(--c-ink-muted))]">Available varieties change throughout the season based on harvest availability.</p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="returns" className="border-b-2 border-[hsl(var(--c-ink))]/25">
                        <AccordionTrigger data-testid={`shop-box-${b.slug}-acc-returns`} className="text-xs uppercase tracking-[0.14em] font-bold text-[hsl(var(--c-ink))] hover:no-underline">Return &amp; Damage Policy</AccordionTrigger>
                        <AccordionContent className="text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">
                          Because mangoes are a fresh, perishable product, we cannot accept returns. If your order arrives damaged, please contact us within 24 hours of delivery with your order number and photos of the damaged fruit and packaging. We&rsquo;ll review the issue and provide a replacement, store credit, or refund when appropriate.
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="shipping" className="border-b-2 border-[hsl(var(--c-ink))]/25">
                        <AccordionTrigger data-testid={`shop-box-${b.slug}-acc-shipping`} className="text-xs uppercase tracking-[0.14em] font-bold text-[hsl(var(--c-ink))] hover:no-underline">Shipping Information</AccordionTrigger>
                        <AccordionContent className="text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">
                          <ul className="space-y-1.5 list-disc pl-4">
                            <li>Carefully packed for freshness.</li>
                            <li>Shipping available throughout the continental U.S., including Central and North Florida.</li>
                            <li>We cannot ship to California, Hawaii, or U.S. territories due to agricultural regulations.</li>
                            <li>Shipping rates are calculated at checkout.</li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="storage">
                        <AccordionTrigger data-testid={`shop-box-${b.slug}-acc-storage`} className="text-xs uppercase tracking-[0.14em] font-bold text-[hsl(var(--c-ink))] hover:no-underline">Ripeness &amp; Storage</AccordionTrigger>
                        <AccordionContent className="text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">
                          Mangoes may arrive firm. Allow them to ripen at room temperature for 2&ndash;5 days. Once ripe, refrigerate and enjoy within 5&ndash;7 days.
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <div className="mt-6 flex-1" />
                    <div className="mt-4">
                      <BoxOrderDialog
                        defaultBox={b.slug}
                        trigger={
                          <SquareButton variant="orange" size="lg" fullWidth testId={`shop-box-order-${b.slug}-button`}>
                            <ShoppingBasket className="h-4 w-4 mr-2" /> Order Now
                          </SquareButton>
                        }
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section data-testid="shop-add-ons" className="block-orange border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="Customize Your Box"
              size="xl"
              title="Add-on options."
              subtitle="Personalize your order with extra mangoes, priority shipping, gift messages, or a curated Grower’s Choice mixed-variety selection."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {ADD_ONS.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.04}>
                <div className="bg-white text-[hsl(var(--c-ink))] p-7 h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:last:border-r-0 lift-on-hover" data-testid={`shop-addon-card-${a.id}`}>
                  <div className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--c-mango-yellow))] border-2 border-[hsl(var(--c-ink))]">
                    <Gift className="h-5 w-5 text-[hsl(var(--c-ink))]" />
                  </div>
                  <div className="mt-5 font-heading text-lg font-extrabold leading-snug text-[hsl(var(--c-ink))]">{a.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ORDER OPTIONS — Shipping / Pickup / Delivery */}
      <section id="order-options" data-testid="shop-order-options" className="block-cream-deep border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="How To Get Your Mangoes"
              size="xl"
              title="Order options."
              subtitle="Nationwide shipping, local pickup in South Florida, or local delivery across the Tri-County area."
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* 1) SHIPPING */}
            <Reveal delay={0.0} className="order-2 md:order-1">
              <div data-testid="shop-option-shipping" className="bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-7 h-full flex flex-col lift-on-hover">
                <div className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--c-mango-orange))] border-2 border-[hsl(var(--c-ink))] text-white">
                  <Truck className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-extrabold">Nationwide &amp; Florida Shipping</h3>
                <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))]">Fresh Florida mangoes shipped directly to your door.</p>
                <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--c-ink-soft))] flex-1">
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-mango-orange))] shrink-0" /> Shipping available throughout the continental United States, including Central and North Florida.</li>
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-mango-orange))] shrink-0" /> Shipping costs are calculated at checkout.</li>
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-mango-orange))] shrink-0" /> Orders are carefully packed to ensure the best possible quality upon arrival.</li>
                </ul>
                <div className="mt-5 border-2 border-[hsl(var(--c-mango-red))] bg-[hsl(var(--c-mango-red))]/8 p-3">
                  <div className="eyebrow text-[hsl(var(--c-mango-red-deep))]">Important</div>
                  <p className="mt-1 text-xs text-[hsl(var(--c-ink))] leading-relaxed">Due to state agricultural regulations, we currently cannot ship fresh mangoes to <strong>California</strong> or <strong>Hawaii</strong>.</p>
                </div>
              </div>
            </Reveal>

            {/* 2) LOCAL PICKUP */}
            <Reveal delay={0.05} className="order-3 md:order-2">
              <div data-testid="shop-option-pickup" className="bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-7 h-full flex flex-col lift-on-hover">
                <div className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--c-leaf))] border-2 border-[hsl(var(--c-ink))] text-white">
                  <MapPin className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-extrabold">Local Pickup</h3>
                <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))]">Perfect for customers located in South Florida.</p>
                <div className="mt-4">
                  <div className="eyebrow text-[hsl(var(--c-ink))]/60">Pricing</div>
                  <div className="mt-1 font-heading text-2xl font-extrabold text-[hsl(var(--c-leaf-deep))]">${LOCAL_DELIVERY.pricePerLb.toFixed(2)} <span className="text-sm font-body font-semibold text-[hsl(var(--c-ink-soft))]">/ lb starting</span></div>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--c-ink-soft))] flex-1">
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-leaf))] shrink-0" /> Pickup available during business operating hours.</li>
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-leaf))] shrink-0" /> Pickup location: Florida SunKissed Mangoes business address.</li>
                  <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 bg-[hsl(var(--c-leaf))] shrink-0" /> Customers will receive pickup instructions with their order confirmation.</li>
                </ul>
              </div>
            </Reveal>

            {/* 3) LOCAL DELIVERY */}
            <Reveal delay={0.1} className="order-1 md:order-3">
              <div data-testid="shop-option-delivery" className="bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-7 h-full flex flex-col lift-on-hover">
                <div className="inline-flex h-12 w-12 items-center justify-center bg-[hsl(var(--c-teal))] border-2 border-[hsl(var(--c-ink))] text-white">
                  <Package className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-extrabold">Local Delivery</h3>
                <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))]">Available throughout South Florida&rsquo;s Tri-County Area.</p>
                <div className="mt-4">
                  <div className="eyebrow text-[hsl(var(--c-ink))]/60">Pricing</div>
                  <div className="mt-1 font-heading text-2xl font-extrabold text-[hsl(var(--c-teal-deep))]">${LOCAL_DELIVERY.pricePerLb.toFixed(2)} <span className="text-sm font-body font-semibold text-[hsl(var(--c-ink-soft))]">/ lb starting</span></div>
                </div>

                <div className="mt-4">
                  <div className="eyebrow text-[hsl(var(--c-ink))]/60">Delivery fees</div>
                  <ul className="mt-2 text-sm divide-y-2 divide-[hsl(var(--c-ink))]/15 border-2 border-[hsl(var(--c-ink))]/15">
                    {DELIVERY_COUNTIES.map((c) => (
                      <li key={c.id} className="flex items-center justify-between px-3 py-2">
                        <span className="text-[hsl(var(--c-ink))] font-semibold">{c.label}</span>
                        <span className={`text-xs font-bold uppercase tracking-wider ${c.fee === 0 ? 'text-[hsl(var(--c-leaf-deep))]' : 'text-[hsl(var(--c-mango-red-deep))]'}`}>{c.fee === 0 ? 'FREE' : `$${c.fee}`}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 space-y-2 flex-1">
                  <div className="border-l-4 border-[hsl(var(--c-leaf))] bg-[hsl(var(--c-leaf))]/8 px-3 py-2 text-xs text-[hsl(var(--c-ink))]"><strong>FREE Local Delivery</strong> on orders of ${LOCAL_DELIVERY.freeThreshold}+.</div>
                  <div className="border-l-4 border-[hsl(var(--c-mango-orange))] bg-[hsl(var(--c-mango-orange))]/8 px-3 py-2 text-xs text-[hsl(var(--c-ink))]"><strong>{LOCAL_DELIVERY.minWeightLbs} lb minimum</strong> for all local delivery orders (Palm Beach, Broward, and Miami-Dade).</div>
                </div>

                <p className="mt-4 text-[10px] uppercase tracking-wider text-[hsl(var(--c-ink-muted))]">Delivery areas may expand in the future.</p>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-12 text-center">
              <p className="text-sm text-[hsl(var(--c-ink-soft))] uppercase tracking-wider font-semibold">Need bulk quantities for your business?</p>
              <div className="mt-4 inline-block">
                <SquareButton to="/wholesale" variant="teal" size="lg" testId="shop-wholesale-cta">View Wholesale Program</SquareButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
